// L1 (event) + L2 (engine.state) を合成して RecordingViewModel の主要 status 群を返す pure derive.
//
// 設計判断 (= log/33/work-log/36 §4):
// 1. server is canonical: 業務 lifecycle (= recording / processing / completed) の真実は L1.
//    L2 は L1 を補助する一時的 hardware orchestration.
// 2. 「engine='completed' && event='processing'」 の中間 gap を 'generating' で表現することで、
//    旧版 (= meetingState='done' の 1 render 窓) の表示消失 regression を構造的に排除.
// 3. transition guard (= flicker 防止) は **caller の useRecordingStatus 側で** 1-bit ratchet を
//    付ける. 本 derive は pure (= 過去を持たない) に保ち、 56 入力組合せを unit test で網羅可能に.

import {
  isCompletedFamily,
  isFailureStatus,
  type YakurekiDetail,
} from '@/shared/types/yakurekiCore'
import {
  RecordingInterruptedError,
  type RecordingEngineState,
} from '@/features/yakureki/recording/types'
import { RecordingSubmitFailedError } from '@/features/yakureki/recording/streaming/apis/streaming'

import type {
  ChecklistStep,
  ConnectionStatus,
  ErrorBannerKind,
  ViewStatus,
} from './types'

interface DerivedCore {
  status: ViewStatus
  connectionStatus: ConnectionStatus
  isMicActive: boolean
  isNavBlocked: boolean
  showRecordingCta: boolean
  showStopCta: boolean
  showPauseCta: boolean
  showResumeCta: boolean
  ctaLabel: string
  checklistVisible: boolean
  checklistStep: ChecklistStep
  checklistAllDone: boolean
  errorBannerKind: ErrorBannerKind
  showRetryCta: boolean
  errorMessage: string | null
  isRetryHopeless: boolean
}

// ─── 中核: ViewStatus ──────────────────────────────────────────────────
// 優先順:
//   1. engine='failed' / event.status in failure → 'failed'
//   2. engine='finalizing' → 'stopping'
//   3. event.status in completed-family → 'completed' (= 業務完了)
//   4. engine in {idle, completed} のとき
//      a. event.status='processing' → 'generating' (= サーバが生成中と言っている)
//      b. event.status='recording' かつ finalize の証拠あり → 'generating'
//         (= finalize ack 後、 server が processing を書く前の 1 poll 窓. ADR-0041)
//   5. engine in {recording, fallback} → 'recording'
//   6. engine='starting' → 'starting'
//   7. else → 'idle' (= event=undefined / pending / 証拠の無い idle × recording)

const deriveStatus = (
  engineState: RecordingEngineState,
  event: YakurekiDetail | undefined,
  hasPendingBlob: boolean,
  hasFinalizeEvidence: boolean
): ViewStatus => {
  // 1. failed 系
  // engine='failed' && pendingBlob 保持中は救済 UX を出すため failed のまま (= reset しない).
  // 注意: caller (= deriveViewStatus) が当事者判定を済ませており、 失敗 session の
  // 非当事者 event ではここに 'failed' は届かない (= 'idle' にマスク済).
  if (engineState === 'failed') return 'failed'
  if (event && isFailureStatus(event.status)) return 'failed'
  // pendingBlob を観測時は status は変わらないが、 caller (= useRecordingStatus) で
  // hasPendingBlob を ViewModel の showRetryCta に変換する.
  void hasPendingBlob

  // 2. stopping (= upload step)
  if (engineState === 'finalizing') return 'stopping'

  // 2.5 paused (log/185 一時停止). pausing/resuming の in-flight も paused として表示し、
  //     button の活性だけ engine.state で gate する (= ViewStatus の増殖を抑える).
  if (
    engineState === 'pausing' ||
    engineState === 'paused' ||
    engineState === 'resuming'
  ) {
    return 'paused'
  }

  // 3. completed (= 業務完了)
  if (event && isCompletedFamily(event.status)) return 'completed'

  // 4. generating (= server 側で処理が進行中の窓). 証拠からだけ出す (ADR-0041):
  //    a. event.status='processing' && engine が録音 hardware を持っていない
  //       (= サーバが生成中と言っている. finalize ack 後の reset 窓もここ)
  //    b. event.status='recording' で、 この端末が finalize ack を受けた証拠がある
  //       (= その render で engine='completed' / 同 hook が completed を観測した記憶 /
  //       別 page の録音完了から遷移してきた hint). server が event.status を 'processing' に
  //       更新する直前の 1 poll 窓で、 旧版は step 5 の「event.status='recording'」 単独条件で
  //       recording に倒れ、 停止直後に「録音中画面」 が一瞬戻る regression が発生していた.
  //       証拠で gate するのは、 同じ idle × recording が 破棄直後 (サーバの PENDING 復帰は
  //       次の poll) / 別端末が録音中の薬歴を開いた / 録音中に閉じた orphan でも成立し、
  //       推定のままだと 3 つとも「SOAP 生成中…」+ 工程 checklist を装うため.
  if (engineState === 'idle' || engineState === 'completed') {
    if (event?.status === 'processing') return 'generating'
    if (
      event?.status === 'recording' &&
      (engineState === 'completed' || hasFinalizeEvidence)
    ) {
      return 'generating'
    }
  }

  // 5. recording (= engine が hardware 的に録音中. stream / fallback batch を圧縮)
  if (engineState === 'recording' || engineState === 'fallback') {
    return 'recording'
  }

  // 6. starting (= mic 起動中)
  if (engineState === 'starting') return 'starting'

  // 7. idle (= 新規 event / pending. 証拠の無い idle × event.status='recording' もここ:
  //    別端末で同 event を録音中、 自端末で sidebar から開いた場合のリアルタイム表示は
  //    本層でなく list の StatusDot で表現する. orphan (録音中に閉じた) も mic を出す)
  return 'idle'
}

// ─── connectionStatus ─────────────────────────────────────────────────
// engine の 'fallback' は「mic + MediaRecorder だけで録音している」という機構の状態で、
// 「streaming が死んだ (= 劣化)」という意味は isIntendedBatch=false の場合にだけ持つ。
// 意図した batch 録音 (log/207 PR-FRONT ①) は同じ機構を使うが劣化ではないため stable。
const deriveConnectionStatus = (
  engineState: RecordingEngineState,
  isIntendedBatch: boolean
): ConnectionStatus =>
  engineState === 'fallback' && !isIntendedBatch ? 'fallback' : 'stable'

// ─── checklist (= 5-step) ─────────────────────────────────────────────
// 設計判断 (= PJ37 Stage 2A の精神を継承):
// - stopping → step 0 (= upload)
// - generating → transcription state ground truth で step 1..3 を決定
// - completed → allDone=true
// - failed → checklist 非表示 (= FailedBanner に切替)
// - legacy batch (= transcription record 不在) は denoise→assess に直行 (= step 3 で fake 居座らない)

const deriveChecklist = (
  status: ViewStatus,
  event: YakurekiDetail | undefined
): { visible: boolean; step: ChecklistStep; allDone: boolean } => {
  if (status === 'stopping') {
    return { visible: true, step: 0, allDone: false }
  }
  if (status === 'completed') {
    return { visible: true, step: 4, allDone: true }
  }
  if (status !== 'generating') {
    return { visible: false, step: 0, allDone: false }
  }
  // generating: transcription で step を決定
  const tx = event?.transcription
  if (!tx) {
    // legacy batch path. processing 全体を assess で表現.
    return { visible: true, step: 3, allDone: false }
  }
  if (tx.status === 'pending') {
    return { visible: true, step: 1, allDone: false }
  }
  if (tx.status === 'running') {
    return { visible: true, step: 2, allDone: false }
  }
  // tx.status === 'completed' || 'failed' → SOAP 生成中
  return { visible: true, step: 3, allDone: false }
}

// ─── errorBanner ──────────────────────────────────────────────────────
const deriveErrorBanner = (
  status: ViewStatus,
  connectionStatus: ConnectionStatus,
  engineError: Error | null,
  hasPendingBlob: boolean,
  isOffline: boolean
): ErrorBannerKind => {
  if (connectionStatus === 'fallback') return 'recoverable'
  // 中断されたが finalize(blob) で SOAP 保全済 (= fireInterruption 成功). status は
  // completed / generating に着地し、 engineError に RecordingInterruptedError を載せて
  // 「中断したが内容は保存された」 info banner を出す. failed ではないため救済ボタンは出さない.
  if (
    engineError instanceof RecordingInterruptedError &&
    (status === 'completed' || status === 'generating')
  ) {
    return 'interrupted-saved'
  }
  // オフライン finalize 待ち: finalize mutation が pause され status='stopping' のまま
  // 固まる (= 無限アップロード). blob は stop() 時点で client 保持済なので、 救済バナーを
  // 'finalize-failed' 経路で出し、 タブ閉じ前に [音声をダウンロード] で保存できるようにする.
  // 復帰 → mutation auto-resume → completed でバナーは自動消滅する.
  if (status === 'stopping' && hasPendingBlob && isOffline) {
    return 'finalize-failed'
  }
  if (status !== 'failed') return 'none'
  if (engineError instanceof RecordingInterruptedError) return 'unrecoverable'
  return 'finalize-failed'
}

// ─── CTA label ────────────────────────────────────────────────────────
const deriveCtaLabel = (status: ViewStatus): string => {
  switch (status) {
    case 'recording':
      return '録音中'
    case 'paused':
      return '一時停止中'
    case 'stopping':
      return '処理中'
    case 'generating':
      return 'SOAP 生成中'
    default:
      return '録音開始'
  }
}

// ─── main entry ───────────────────────────────────────────────────────
export const deriveViewStatus = (params: {
  engineState: RecordingEngineState
  event: YakurekiDetail | undefined
  /**
   * engine の現 session (失敗時は失敗 session) が対象にしていた event id 群
   * (= engine.recordingEventIds). failed の当事者判定に使う.
   */
  engineEventIds: string[]
  engineError: Error | null
  hasPendingBlob: boolean
  isOffline: boolean
  /**
   * engine の現録音が「意図した batch 経路」か (= engine.isIntendedBatch)。
   * true なら engineState='fallback' を劣化として扱わない (黄バナー抑止)。
   */
  isIntendedBatch: boolean
  /**
   * この端末が表示中 event の finalize ack を受けた証拠 (ADR-0041)。同 hook instance が
   * engine='completed' を観測した記憶、 または別 page の録音完了から遷移してきた hint。
   * step 4b (idle × event.status='recording' → generating) はこれが無いと立たない。
   * caller (useRecordingStatus) が過去を持ち、 本 derive は pure のまま。
   */
  hasFinalizeEvidence: boolean
}): DerivedCore => {
  const {
    engineState,
    event,
    engineEventIds,
    engineError,
    hasPendingBlob,
    isOffline,
    isIntendedBatch,
    hasFinalizeEvidence,
  } = params

  // 当事者判定: 表示中 event が engine の session の対象かどうか.
  // engine は page instance 単位 (= /soap/:requestId は同一 instance で患者を跨ぐ) のため、
  // failed を無条件に反映すると 1 回の finalize 失敗が別患者の録音開始まで塞いでしまう.
  // - event 未定 (= フリー録音 page / event fetch 中) は当事者扱い (= 従来挙動維持)
  // - engineEventIds 空 (= session 確立前の失敗など) も当事者扱い (= 安全側: 救済 UX を消さない)
  const isEnginePartyEvent =
    !event || engineEventIds.length === 0 || engineEventIds.includes(event.id)

  // 非当事者 event に対しては engine の failed / paused 系を 'idle' として扱う (= この event に
  // とって engine は何もしていないのと等価). 当事者 event に戻れば failed + 救済 UX /
  // paused + 再開導線が再表示される.
  const isMaskableState =
    engineState === 'failed' ||
    engineState === 'pausing' ||
    engineState === 'paused' ||
    engineState === 'resuming'
  const effectiveEngineState: RecordingEngineState =
    isMaskableState && !isEnginePartyEvent ? 'idle' : engineState

  const status = deriveStatus(
    effectiveEngineState,
    event,
    hasPendingBlob,
    hasFinalizeEvidence
  )
  const connectionStatus = deriveConnectionStatus(
    effectiveEngineState,
    isIntendedBatch
  )
  const checklist = deriveChecklist(status, event)
  // engineError も当事者 event でのみ表示に載せる (= 非当事者 page への error 文言の漏出防止).
  const effectiveEngineError = isEnginePartyEvent ? engineError : null
  // pendingBlob も当事者 event でのみ観測させる. 非当事者 event 自身の status が失敗系の場合、
  // status='failed' × 生 hasPendingBlob で showRetryCta が立ち、 他患者の blob への
  // DL/再試行導線がこの page に漏れてしまう (= blob は常に失敗 session の当事者のもの).
  const effectiveHasPendingBlob = isEnginePartyEvent ? hasPendingBlob : false
  const errorBannerKind = deriveErrorBanner(
    status,
    connectionStatus,
    effectiveEngineError,
    effectiveHasPendingBlob,
    isOffline
  )

  return {
    status,
    connectionStatus,
    isMicActive:
      effectiveEngineState === 'starting' ||
      effectiveEngineState === 'recording' ||
      effectiveEngineState === 'fallback' ||
      // pausing はまだマイクを握っている (teardown は POST 成功後). resuming は再取得中.
      effectiveEngineState === 'pausing' ||
      effectiveEngineState === 'resuming',
    // paused は nav block しない (= 一時停止中に別患者の録音が可能. log/185).
    // ただし pausing/resuming の in-flight 中は blob/通信が宙にあるため block する.
    isNavBlocked:
      status === 'starting' ||
      status === 'recording' ||
      status === 'stopping' ||
      effectiveEngineState === 'pausing' ||
      effectiveEngineState === 'resuming',
    showRecordingCta: status === 'idle',
    showStopCta: status === 'recording',
    // 一時停止は streaming 経路が健在 (= stable) なときだけ出す. fallback 中は streaming
    // session が放棄済みで pause が成立しない.
    showPauseCta:
      status === 'recording' && effectiveEngineState === 'recording',
    // paused 確定中のみ活性 (pausing/resuming の in-flight 中は非活性).
    showResumeCta: effectiveEngineState === 'paused',
    ctaLabel: deriveCtaLabel(status),
    checklistVisible: checklist.visible,
    checklistStep: checklist.step,
    checklistAllDone: checklist.allDone,
    errorBannerKind,
    // failed の retry/DL 救済に加え、 オフライン finalize 待ち (status='stopping') でも
    // pendingBlob を握っているので救済バナー (= DL 主体) を出す. RecordingFailedAlert 側で
    // オフライン中は retry を隠し DL を強調する.
    showRetryCta:
      effectiveHasPendingBlob &&
      (status === 'failed' || (status === 'stopping' && isOffline)),
    // UI に出す error 文言. RecordingInterruptedError は専用バナー (unrecoverable /
    // interrupted-saved) が文言を持つため載せない.
    errorMessage:
      effectiveEngineError instanceof RecordingInterruptedError
        ? null
        : effectiveEngineError?.message ?? null,
    // 「同じ blob の再送信では回復する見込みが薄い」失敗 (= 409/4xx).
    // RecordingFailedAlert が再試行ボタンを隠して DL 誘導に切り替える.
    isRetryHopeless:
      effectiveEngineError instanceof RecordingSubmitFailedError &&
      effectiveEngineError.isRetryHopeless,
  }
}
